import { useEditBriefInfluencerNotes } from "@entities/brief/lib/hooks/useEditBriefInfluencerNotes";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { Button } from "@shared/ui/Button";
import { Input, Space, Tooltip } from "antd";
import classNames from "classnames";
import { Check, PenLine, X } from "lucide-react";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

export interface NotesCellProps {
  record: IBriefInfluencerV2;
  refetch?: () => void;
}

export function NotesCell({ record, refetch }: NotesCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const currentAgencyNote = record.agencySharedBrandNotes || "";
  const [agencyNote, setAgencyNote] = useState(currentAgencyNote);

  const { editNotes, isEditingNotes } = useEditBriefInfluencerNotes(
    record.briefId,
    () => {
      refetch?.();
      setIsEditing(false);
    }
  );

  useEffect(() => {
    setAgencyNote(currentAgencyNote);
  }, [currentAgencyNote]);

  const handleSave = () => {
    const trimmed = agencyNote.trim();
    if (trimmed !== currentAgencyNote.trim()) {
      editNotes({
        briefInfluencerId: record._id,
        agencySharedBrandNotes: trimmed,
      });
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setAgencyNote(currentAgencyNote);
  };

  const brandNoteText = record.brandNotes || "";

  return (
    <div className={styles.notesCellContainer}>
      {/* Brand Section (Read-Only in Platform - TOP displays brandNotes) */}
      <div className={styles.notesSection}>
        <div className={styles.notesHeaderRow}>
          <span className={styles.notesHeaderBrand}>Brand</span>
        </div>
        {brandNoteText ? (
          <Tooltip title={brandNoteText} placement="top" mouseEnterDelay={0.2}>
            <p className={styles.notesTextBrand}>{brandNoteText}</p>
          </Tooltip>
        ) : (
          <p className={styles.notesTextBrand}>
            <span className={styles.emptyNotesPlaceholder}>—</span>
          </p>
        )}
      </div>

      {/* Agency Section (Editable in Platform - BOTTOM displays agencySharedBrandNotes) */}
      <div className={styles.notesDivider}>
        <div
          className={classNames(styles.notesEditableBox, {
            [styles.notesEditableBoxActive]: isEditing,
          })}
          onClick={(e) => {
            if (!isEditing) {
              e.stopPropagation();
              setIsEditing(true);
            }
          }}
        >
          <div className={styles.notesHeaderRow}>
            <span className={styles.notesHeaderAgency}>Agency</span>
            {isEditing ? (
              <Space size={2} onClick={(e) => e.stopPropagation()}>
                <Button
                  icon={<Check size={10} />}
                  type="text"
                  size="small"
                  loading={isEditingNotes}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSave();
                  }}
                  className={styles.editActionButton}
                />
                <Button
                  icon={<X size={10} />}
                  type="text"
                  size="small"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCancel();
                  }}
                  className={styles.editActionButton}
                />
              </Space>
            ) : (
              <PenLine size={11} className={styles.notesPenIcon} />
            )}
          </div>
          {isEditing ? (
            <Input.TextArea
              value={agencyNote}
              onChange={(e) => setAgencyNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSave();
                } else if (e.key === "Escape") {
                  handleCancel();
                }
              }}
              disabled={isEditingNotes}
              autoFocus
              rows={2}
              className={styles.notesInput}
              onClick={(e) => e.stopPropagation()}
            />
          ) : currentAgencyNote ? (
            <Tooltip title={currentAgencyNote} placement="top" mouseEnterDelay={0.2}>
              <p className={styles.notesTextAgency}>{currentAgencyNote}</p>
            </Tooltip>
          ) : (
            <p className={styles.notesTextAgency}>
              <span className={styles.emptyNotesPlaceholder}>Add agency note...</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
